import Phaser from 'phaser';
import { TrippyPostFX } from './TrippyPostFX';

const DRIFT_MS = 1200;
const BURST_MIN_GAP_MS = 12_000;
const BURST_MAX_GAP_MS = 28_000;

export type TripBurst = 'invert' | 'aberration' | 'acid' | 'vignette';

const BURST_DURATIONS: Record<TripBurst, number> = {
  invert: 1000,
  aberration: 1400,
  acid: 2400,
  vignette: 800,
};

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export class TrippyController {
  private filter: TrippyPostFX | null = null;
  private currentHue = 0;
  private targetHue = 0;
  private currentSat = 1.0;
  private targetSat = 1.0;
  private active = !prefersReducedMotion();
  private gated = false;
  private burstType: TripBurst | null = null;
  private burstTime = 0;
  private burstDuration = 0;
  private elapsedMs = 0;
  private nextBurstAt = 0;

  constructor(private scene: Phaser.Scene) {
    if (!this.active) return;
    if (scene.sys.renderer.type !== Phaser.WEBGL) {
      this.active = false;
      return;
    }
    try {
      const cam = scene.cameras.main;
      this.filter = new TrippyPostFX(cam);
      cam.filters.external.add(this.filter);
    } catch (e) {
      console.warn('TrippyPostFX failed to attach; trippy effects disabled', e);
      this.active = false;
      this.filter = null;
    }
    this.scheduleNextBurst();
  }

  setLevel(level: number): void {
    if (!this.active || !this.filter) return;
    const cycle = ((level * 14) % 360) - 180;
    const clampedDeg = Math.max(-25, Math.min(25, cycle * 25 / 180));
    this.targetHue = clampedDeg * Math.PI / 180;
    this.targetSat = 1.0 + 0.15 * Math.sin(level);
  }

  setGated(gated: boolean): void {
    this.gated = gated;
    if (gated && this.filter) {
      // Snap any in-flight burst back to rest immediately so the player isn't
      // disoriented at decision moments (capture/fail/game-over).
      this.burstType = null;
      this.burstTime = 0;
      this.filter.uInvertMix = 0;
      this.filter.uAberration = 0;
      this.filter.uVignette = 0;
    }
  }

  trigger(type: TripBurst): void {
    if (!this.active || !this.filter) return;
    this.burstType = type;
    this.burstTime = 0;
    this.burstDuration = BURST_DURATIONS[type];
  }

  tick(deltaMs: number): void {
    if (!this.active || !this.filter) return;
    this.elapsedMs += deltaMs;

    // Drift always lerps toward target — invisible at rest.
    const k = Math.min(1, deltaMs / DRIFT_MS);
    this.currentHue += (this.targetHue - this.currentHue) * k;
    this.currentSat += (this.targetSat - this.currentSat) * k;
    this.filter.uHue = this.currentHue;
    this.filter.uSat = this.currentSat;

    if (this.gated) return;

    if (this.burstType) {
      this.burstTime += deltaMs;
      this.applyBurst(this.burstType, this.burstTime / this.burstDuration);
      if (this.burstTime >= this.burstDuration) {
        this.burstType = null;
        this.filter.uInvertMix = 0;
        this.filter.uAberration = 0;
        this.filter.uVignette = 0;
        this.scheduleNextBurst();
      }
    } else if (this.elapsedMs >= this.nextBurstAt) {
      const types: TripBurst[] = ['invert', 'aberration', 'acid', 'vignette'];
      this.trigger(types[Math.floor(Math.random() * types.length)]);
    }
  }

  private scheduleNextBurst(): void {
    const gap = BURST_MIN_GAP_MS + Math.random() * (BURST_MAX_GAP_MS - BURST_MIN_GAP_MS);
    this.nextBurstAt = this.elapsedMs + gap;
  }

  private applyBurst(type: TripBurst, t: number): void {
    if (!this.filter) return;
    // Triangle envelope: 0 → 1 → 0 across the burst's duration.
    const tri = t < 0.5 ? t * 2 : Math.max(0, 1 - (t - 0.5) * 2);
    switch (type) {
      case 'invert':
        this.filter.uInvertMix = 0.7 * tri;
        break;
      case 'aberration':
        this.filter.uAberration = 8 * tri;
        break;
      case 'acid':
        // Rapid hue cycle on top of drift; saturation bump.
        this.filter.uHue = this.currentHue + (t * Math.PI * 2);
        this.filter.uSat = this.currentSat + 0.4 * tri;
        break;
      case 'vignette':
        this.filter.uVignette = tri;
        break;
    }
  }

  destroy(): void {
    if (!this.filter) return;
    try {
      this.scene.cameras.main.filters.external.remove(this.filter);
    } catch {
      // Camera or filter list may already be torn down — best-effort.
    }
    this.filter = null;
  }
}
